/**
 * `table.grid-tbl` → `grid-table` (process/08-COMPONENT-CATALOGUE.md). Not a component (no JSX) —
 * a class-string constant, the same convention `formControls.ts`'s `TEXT_INPUT_CLASSNAME` already
 * uses for a DOM element the catalogue names but that isn't its own React component, since every
 * screen builds its own `<table>` around its own columns.
 *
 * **Found and fixed W180** — every hand-rolled `<table data-cmp="grid-table">` in this app (`v-health`,
 * `v-dashboard`, `v-drift`, `ReportLayer`, and others) had been styled ad hoc, with none of them
 * matching the prototype's own generic `table`/`th`/`td` rules or `.grid-tbl`'s own header/zebra/hover
 * overrides — the table looked, at every size and weight, like plain body text with a border, not a
 * data grid. Sourced directly from Prototype_v2.html:
 *
 * `table{width:100%;border-collapse:collapse;font-size:12.5px}` → `--fs-body-lg` (13px, +0.5px delta,
 * the same tolerance already accepted project-wide). `th{text-align:left;font-size:10.5px;
 * letter-spacing:.4px;text-transform:uppercase;color:var(--muted);font-weight:700;padding:8px 10px;
 * border-bottom:1px solid var(--bd)}` — 10.5px → `--fs-small` (11px, +0.5px delta, the same mapping
 * `Chip.tsx`'s own `default` variant already uses for this exact source value). `.grid-tbl th`
 * overrides padding to `8px 12px` and border colour to `--bd2`, background white. `td{padding:9px
 * 10px;border-bottom:1px solid #EFEFF0;color:var(--tx2);vertical-align:top}` — `#EFEFF0` folds into
 * `--row` (`process/11-TOKEN-MAP.md`, Δ3). `.grid-tbl td` overrides padding to `8px 12px`, no border.
 * `.grid-tbl tbody tr:nth-child(even){background:var(--row)}` → Tailwind's `even:` modifier.
 * `.grid-tbl tbody tr:hover td{background:var(--sel)}` → `hover:bg-sel` on the row itself, since
 * every row here is already a plain background with no per-cell colour of its own to fight.
 *
 * **Row dividers added, not sourced from the prototype (reported live: "add good borders... modern
 * like MUI").** The prototype's own `.grid-tbl` relies on zebra shading alone with no border between
 * rows; every `<td>` now also carries a `border-b`, the same `--bd2` the header row's own divider
 * already uses, matching MUI's default `Table`/`TableCell` convention (a hairline under every row,
 * not just the header) rather than replacing the existing zebra shading — both together, not one
 * instead of the other. No outer/vertical borders on the table itself: about a third of this
 * component's 29 call sites already sit inside their own `panel` (its own border), and MUI's own
 * default `Table` has no outer or column borders either — only `TableContainer`/`Paper` (this
 * product's `panel`) owns that.
 */
export const GRID_TABLE_CLASSNAME = 'w-full border-collapse text-body-lg'
export const GRID_TABLE_HEAD_ROW_CLASSNAME = 'text-left'
export const GRID_TABLE_TH_CLASSNAME = 'py-3 px-5 text-small font-bold uppercase tracking-wide text-muted bg-panel border-b border-bd2'
export const GRID_TABLE_ROW_CLASSNAME = 'even:bg-row hover:bg-sel'
export const GRID_TABLE_TD_CLASSNAME = 'py-3 px-5 align-top text-tx2 border-b border-bd2'
